import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    phone: '',
    city: '',
    program: '',
    enrollmentDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+971', country: 'UAE' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'Australia' },
    { code: '+65', country: 'Singapore' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+968', country: 'Oman' },
    { code: '+973', country: 'Bahrain' },
    { code: '+965', country: 'Kuwait' },
    { code: '+92', country: 'Pakistan' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+94', country: 'Sri Lanka' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: `${formData.countryCode} ${formData.phone}`,
        city: formData.city,
        program: formData.program,
        enrollmentDate: formData.enrollmentDate
      };
      
      const response = await axios.post(`${API}/lead`, submissionData);
      
      if (response.status === 200) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Sorry, there was an error. Please try WhatsApp or email us at connect@dreambigeducation.co');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'm interested in studying in Dubai. Please share details.");
    const whatsappURL = `https://wa.me/919321007830?text=${message}`;
    window.open(whatsappURL, '_blank');
  };

  return (
    <div className="connect-page">
      {/* Header */}
      <header className="connect-header">
        <div className="connect-header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_dubai-education/artifacts/j7nrhfcz_Copy%20of%20Blue%20and%20Gold%20Minimalist%20Education%20Logo.png" 
            alt="Dream Big Education" 
            className="connect-logo"
            onClick={() => window.location.href = '/'}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="connect-main">
        <div className="connect-container">
          <div className="connect-content">
            {/* Left Side - Form */}
            <div className="connect-section connect-form-section">
              <h1 className="connect-headline">Love that you're thinking big!</h1>
              <p className="connect-subheadline">Submit the form for personalized guidance on studying in Dubai.</p>
              
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="lead-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="Enter first name"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Enter last name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="country-code-select"
                      >
                        {countryCodes.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.code} {item.country}
                          </option>
                        ))}
                      </select>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="Enter your city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="program">Program Interested In *</label>
                    <select
                      id="program"
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="form-select"
                    >
                      <option value="">Select a program</option>
                      <option value="Undergraduate">Undergraduate</option>
                      <option value="Masters">Masters</option>
                      <option value="MBA">MBA</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="enrollmentDate">When do you want to enroll? *</label>
                    <select
                      id="enrollmentDate"
                      name="enrollmentDate"
                      value={formData.enrollmentDate}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="form-select"
                    >
                      <option value="">Select enrollment date</option>
                      <option value="Sept 2026">Sept 2026</option>
                      <option value="Jan 2027">Jan 2027</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <Button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              ) : (
                <div className="success-message">
                  <div className="success-icon">🎉</div>
                  <h2>Congrats!</h2>
                  <p>You've just taken the first step toward studying in Dubai. Our admissions advisor will contact you shortly to guide you through the next steps.</p>
                  <Button 
                    onClick={() => window.location.href = 'https://dreambigeducation.co/'}
                    className="home-button"
                  >
                    Take me to Dream Big's Website
                  </Button>
                </div>
              )}
            </div>

            {/* Right Side - WhatsApp */}
            <div className="connect-section connect-whatsapp-section">
              <div className="whatsapp-card">
                <div className="whatsapp-icon">
                  <MessageCircle size={60} />
                </div>
                <h2>Prefer WhatsApp?</h2>
                <p>Get instant answers to your questions. Chat with our admissions team now!</p>
                <Button 
                  onClick={handleWhatsApp}
                  className="whatsapp-button"
                >
                  <MessageCircle className="mr-2" size={20} />
                  Chat on WhatsApp
                </Button>
                <p className="whatsapp-note">We typically respond within 5 minutes during business hours</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConnectPage;
